// PRC-016 · A3: el enlace del email trae aquí (/cuenta-confirmada).
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { supabase } from '../../../config/supabaseClient';
import { SimpleLayout } from '../components/JourneyLayouts';
import { JourneyField, Notice, StateIcon, StatusBlock } from '../components/JourneyBlocks';
import { Eyebrow, JourneyButton, JourneyText } from '../components/JourneyBasics';
import { J } from '../journeyTokens';
import { linkErrorInUrl, markFirstAccess, OFFLINE_MESSAGE, resendConfirmation } from '../journeyAuth';

const REDIRECT_SECONDS = 5;
// Tiempo máximo para que Supabase lea la sesión del enlace antes de darlo por no válido.
const SESSION_WAIT_MS = 6000;

const ResendBox: React.FC = () => {
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);
  const send = async () => {
    if (!email.trim()) return;
    const r = await resendConfirmation(email.trim());
    setMsg(r === 'sent'
      ? { tone: 'ok', text: 'Si la cuenta existe y aún no está confirmada, te hemos enviado un enlace nuevo.' }
      : r === 'limited'
        ? { tone: 'warn', text: 'Acabamos de enviarte uno. Podrás pedir otro dentro de un minuto.' }
        : { tone: 'danger', text: r === 'offline' ? OFFLINE_MESSAGE : 'No hemos podido enviar el enlace. Inténtalo en unos minutos.' });
  };
  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px', textAlign: 'left' }}>
      <JourneyField id="a3-email" label="Email de tu cuenta" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <JourneyButton onClick={send} disabled={!email.trim()}>Enviar enlace nuevo</JourneyButton>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
    </Box>
  );
};

const AccountConfirmedPage: React.FC = () => {
  const [state, setState] = useState<'checking' | 'confirmed' | 'invalid'>(() => (linkErrorInUrl() ? 'invalid' : 'checking'));
  const [company, setCompany] = useState<string | null>(null);
  const [left, setLeft] = useState(REDIRECT_SECONDS);
  const [askNew, setAskNew] = useState(false);

  useEffect(() => {
    if (state !== 'checking') return;
    let done = false;
    const accept = (session: any) => {
      if (done || !session?.user?.email_confirmed_at) return;
      done = true;
      setCompany(session.user.user_metadata?.company_name ?? null);
      // PRC-025: la entrada que sigue es el primer acceso de la cuenta.
      markFirstAccess();
      setState('confirmed');
    };
    supabase.auth.getSession().then(({ data }) => accept(data.session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => accept(session));
    const timeout = window.setTimeout(() => { if (!done) setState('invalid'); }, SESSION_WAIT_MS);
    return () => { subscription.unsubscribe(); window.clearTimeout(timeout); };
  }, [state]);

  useEffect(() => {
    if (state !== 'confirmed') return;
    if (left <= 0) { window.location.href = '/'; return; }
    const t = window.setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [state, left]);

  return (
    <SimpleLayout narrow>
      {state === 'checking' && <StatusBlock variant="loading" minHeight={360} title="Comprobando el enlace" text="Un momento." />}
      {state === 'confirmed' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '20px' }}>
          <StateIcon tone="ok"><CheckCircleOutlineIcon /></StateIcon>
          <Eyebrow both>Paso 3 de 3</Eyebrow>
          <JourneyText variant="title">Email confirmado</JourneyText>
          <JourneyText variant="lead">
            {company ? <>La cuenta de <b style={{ color: J.text }}>{company}</b> está activa.</> : 'Tu cuenta está activa.'} Al entrar empieza tu prueba de 30 días.
          </JourneyText>
          <JourneyButton sx={{ minWidth: 260 }} onClick={() => { window.location.href = '/'; }}>Entrar en PROA Pricing</JourneyButton>
          <JourneyText variant="small">Te llevamos automáticamente en {left} {left === 1 ? 'segundo' : 'segundos'}.</JourneyText>
        </Box>
      )}
      {state === 'invalid' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '20px' }}>
          <StateIcon tone="warn"><LinkOffIcon /></StateIcon>
          <JourneyText variant="title">Este enlace ya no sirve</JourneyText>
          <JourneyText variant="lead">Este enlace ha caducado o ya se ha usado. Si ya confirmaste tu email, inicia sesión. Si no, pide un enlace nuevo.</JourneyText>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
            <JourneyButton onClick={() => { window.location.href = '/login'; }}>Iniciar sesión</JourneyButton>
            <JourneyButton kind="ghost" onClick={() => setAskNew(true)}>Pedir un enlace nuevo</JourneyButton>
          </Box>
          {askNew && <ResendBox />}
        </Box>
      )}
    </SimpleLayout>
  );
};

export default AccountConfirmedPage;
